/**
 * User settings — GitHub integration.
 *
 * Lets the signed-in user save a read-only PAT, configure which repos to sync,
 * and trigger a manual sync. Linked PRs + commits are auto-attached to tickets
 * via branch-name prefix (every branch starts with the ticket key, e.g.
 * `APO-12-fix-foo` links to APO-12).
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState, type CSSProperties } from "react";
import { Link, useLocation } from "react-router-dom";

import { AtlasModelCard } from "@/components/AtlasModelCard";
import { MfaCard } from "@/components/MfaCard";
import { TenantBrandingCard } from "@/components/TenantBrandingCard";
import { api } from "@/lib/api";
import type { EmployeeProfile, GithubConnection, GithubSyncResult } from "@/lib/types";

export function UserSettingsPage() {
  const queryClient = useQueryClient();
  const location = useLocation();
  const mfaSectionRef = useRef<HTMLDivElement | null>(null);

  // Topbar's shield icon links to /settings#mfa — bring the card into view so
  // an admin who clicks it lands directly on the device list.
  useEffect(() => {
    if (location.hash !== "#mfa") return;
    mfaSectionRef.current?.scrollIntoView({ block: "start", behavior: "smooth" });
  }, [location.hash]);
  const { data: conn, isLoading } = useQuery({
    queryKey: ["me-github"],
    queryFn: async () => (await api.get<GithubConnection>("/me/github")).data,
  });

  // Snapshot of the profile so the Profile card can show "Complete" vs
  // "Optional — add details" without forcing a full page load.
  const { data: profile } = useQuery({
    queryKey: ["my-profile"],
    queryFn: async () => (await api.get<EmployeeProfile>("/me/profile")).data,
  });

  const [pat, setPat] = useState("");
  const [defaultRepo, setDefaultRepo] = useState("");
  const [syncRepos, setSyncRepos] = useState("");
  const [bannerError, setBannerError] = useState<string | null>(null);
  const [syncSummary, setSyncSummary] = useState<GithubSyncResult | null>(null);

  // Hydrate the form whenever the connection refreshes.
  useEffect(() => {
    if (!conn) return;
    setDefaultRepo(conn.default_repo ?? "");
    setSyncRepos(conn.sync_repos.join(", "));
  }, [conn]);

  const connect = useMutation({
    mutationFn: async () => {
      const body = {
        pat: pat.trim(),
        default_repo: defaultRepo.trim() || null,
        sync_repos:
          syncRepos
            .split(/[\s,]+/)
            .map((r) => r.trim())
            .filter(Boolean) || null,
      };
      return (await api.put<GithubConnection>("/me/github", body)).data;
    },
    onSuccess: () => {
      setPat("");
      setBannerError(null);
      queryClient.invalidateQueries({ queryKey: ["me-github"] });
    },
    onError: (err) => {
      const detail =
        (err as { response?: { data?: { detail?: string } } })?.response?.data
          ?.detail ?? "Could not save GitHub PAT.";
      setBannerError(detail);
    },
  });

  const disconnect = useMutation({
    mutationFn: async () => api.delete("/me/github"),
    onSuccess: () => {
      setPat("");
      setSyncSummary(null);
      queryClient.invalidateQueries({ queryKey: ["me-github"] });
    },
  });

  const sync = useMutation({
    mutationFn: async () => (await api.post<GithubSyncResult>("/me/github/sync")).data,
    onSuccess: (result) => {
      setSyncSummary(result);
      setBannerError(null);
      queryClient.invalidateQueries({ queryKey: ["me-github"] });
      queryClient.invalidateQueries({ queryKey: ["my-progress"] });
      queryClient.invalidateQueries({ queryKey: ["issue-links"] });
    },
    onError: (err) => {
      const detail =
        (err as { response?: { data?: { detail?: string } } })?.response?.data
          ?.detail ?? "Sync failed.";
      setBannerError(detail);
    },
  });

  return (
    <div style={{ maxWidth: 720 }}>
      <header style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 28, fontWeight: 600, letterSpacing: "-0.3px", margin: 0 }}>
          Settings
        </h1>
        <p style={{ color: "var(--mute)", fontSize: 13, marginTop: 6 }}>
          Manage your profile and account integrations.
        </p>
      </header>

      {/* Profile — optional. Edit at /settings/profile. */}
      <section style={card}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
          <h2 style={{ fontSize: 16, fontWeight: 600, margin: 0 }}>Profile</h2>
          {profile?.is_complete ? (
            <span
              style={{
                fontSize: 10.5,
                fontWeight: 500,
                letterSpacing: 0.6,
                textTransform: "uppercase",
                padding: "2px 8px",
                background: "rgba(0,215,34,0.12)",
                color: "#047a18",
                borderRadius: 3,
              }}
            >
              Complete
            </span>
          ) : (
            <span
              style={{
                fontSize: 10.5,
                fontWeight: 500,
                letterSpacing: 0.6,
                textTransform: "uppercase",
                padding: "2px 8px",
                background: "var(--surface-2)",
                color: "var(--body-mid)",
                borderRadius: 3,
              }}
            >
              Optional
            </span>
          )}
        </div>
        <p style={{ fontSize: 13, color: "var(--body-mid)", margin: "0 0 16px" }}>
          Phone, address, resume, visa status, emergency contact, and other
          consultancy details. Entirely optional — fill what you want.
          Visible to your tenant admin and the platform owners.
        </p>
        <Link to="/settings/profile" style={{ ...btnPrimary, display: "inline-block", textDecoration: "none" }}>
          {profile?.is_complete ? "Edit profile" : "Add profile details"}
        </Link>
      </section>

      {/* Two-factor authentication — available to everyone. */}
      <div ref={mfaSectionRef} id="mfa" style={{ scrollMarginTop: 64 }}>
        <MfaCard />
      </div>

      {/* Admin-only: company logo upload. The card returns null for
          non-admin users so this section auto-hides. */}
      <TenantBrandingCard />

      {/* Super-admin-only: pick the Atlas coding model. Auto-hides otherwise. */}
      <AtlasModelCard />

      <section style={card}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
          <h2 style={{ fontSize: 16, fontWeight: 600, margin: 0 }}>GitHub</h2>
          {conn?.connected && (
            <span
              style={{
                fontSize: 10.5,
                fontWeight: 500,
                letterSpacing: 0.6,
                textTransform: "uppercase",
                padding: "2px 8px",
                background: "rgba(0,215,34,0.12)",
                color: "#047a18",
                borderRadius: 3,
              }}
            >
              Connected
            </span>
          )}
        </div>
        <p style={{ fontSize: 13, color: "var(--body-mid)", margin: "0 0 16px" }}>
          Save a read-only PAT and minute2sec will auto-link your PRs and commits to tickets.
          <br />
          <span style={{ color: "var(--mute)" }}>
            Branch-name convention: every branch starts with the ticket key, e.g.{" "}
            <code style={code}>APO-12-fix-rendering</code> links to{" "}
            <strong style={{ color: "var(--ink)" }}>APO-12</strong>.
          </span>
        </p>

        {isLoading ? (
          <p style={{ color: "var(--mute)" }}>Loading…</p>
        ) : (
          <>
            {conn?.connected && (
              <div
                style={{
                  background: "var(--surface-1)",
                  border: "1px solid var(--hairline)",
                  borderRadius: 6,
                  padding: 12,
                  marginBottom: 16,
                  fontSize: 13,
                  color: "var(--body)",
                }}
              >
                Connected as{" "}
                <strong style={{ color: "var(--ink)" }}>@{conn.github_username}</strong>
                {conn.last_synced_at && (
                  <span style={{ color: "var(--mute)" }}>
                    {" · last synced "}
                    {new Date(conn.last_synced_at).toLocaleString()}
                  </span>
                )}
              </div>
            )}

            <label style={fieldRow}>
              <span style={fieldLabel}>
                {conn?.connected ? "Replace PAT (leave blank to keep current)" : "Personal access token"}
              </span>
              <input
                type="password"
                placeholder="ghp_… (read-only — needs read:user + repo)"
                value={pat}
                onChange={(e) => setPat(e.target.value)}
                style={{ ...input, fontFamily: "var(--font-mono)" }}
                autoComplete="off"
              />
              <span style={hint}>
                Create at{" "}
                <a
                  href="https://github.com/settings/tokens?type=beta"
                  target="_blank"
                  rel="noreferrer"
                  style={{ color: "var(--ink)" }}
                >
                  github.com/settings/tokens
                </a>
                . Stored encrypted at rest.
              </span>
            </label>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <label>
                <span style={fieldLabel}>Default repo</span>
                <input
                  type="text"
                  placeholder="org/repo"
                  value={defaultRepo}
                  onChange={(e) => setDefaultRepo(e.target.value)}
                  style={{ ...input, fontFamily: "var(--font-mono)" }}
                />
              </label>
              <label>
                <span style={fieldLabel}>Additional repos (comma-separated)</span>
                <input
                  type="text"
                  placeholder="org/repo1, org/repo2"
                  value={syncRepos}
                  onChange={(e) => setSyncRepos(e.target.value)}
                  style={{ ...input, fontFamily: "var(--font-mono)" }}
                />
              </label>
            </div>

            {bannerError && (
              <p role="alert" style={alertStyle}>
                {bannerError}
              </p>
            )}

            {syncSummary && (
              <div
                style={{
                  marginTop: 12,
                  padding: "10px 12px",
                  fontSize: 13,
                  color: "#047a18",
                  background: "rgba(0,215,34,0.06)",
                  border: "1px solid rgba(0,215,34,0.25)",
                  borderRadius: 4,
                }}
              >
                Synced {syncSummary.repos_synced.length} repo
                {syncSummary.repos_synced.length === 1 ? "" : "s"} ·{" "}
                <strong>{syncSummary.prs_upserted}</strong> PRs (
                {syncSummary.prs_linked} linked to tickets) ·{" "}
                <strong>{syncSummary.commits_upserted}</strong> commits (
                {syncSummary.commits_linked} linked).
                {syncSummary.skipped_repos.length > 0 && (
                  <div style={{ marginTop: 6, color: "var(--body-mid)" }}>
                    Skipped:{" "}
                    {syncSummary.skipped_repos
                      .map((s) => `${s.repo} (${s.reason})`)
                      .join("; ")}
                  </div>
                )}
              </div>
            )}

            <div style={{ display: "flex", gap: 8, marginTop: 18, flexWrap: "wrap" }}>
              <button
                type="button"
                onClick={() => {
                  if (!pat.trim() && !conn?.connected) {
                    setBannerError("Paste a PAT to connect GitHub.");
                    return;
                  }
                  if (!pat.trim() && conn?.connected) {
                    // Update repos without re-supplying PAT — call PUT with empty pat? We need to
                    // require PAT for safety. Offer "Save repos only" via a separate path later;
                    // for V1, require PAT to write.
                    setBannerError(
                      "Paste your PAT again to save changes (or click Sync if you only want to refresh).",
                    );
                    return;
                  }
                  setBannerError(null);
                  connect.mutate();
                }}
                disabled={connect.isPending}
                style={{
                  ...btnPrimary,
                  opacity: connect.isPending ? 0.6 : 1,
                  cursor: connect.isPending ? "wait" : "pointer",
                }}
              >
                {connect.isPending ? "Saving…" : conn?.connected ? "Save changes" : "Connect"}
              </button>
              {conn?.connected && (
                <>
                  <button
                    type="button"
                    onClick={() => sync.mutate()}
                    disabled={sync.isPending}
                    style={{
                      ...btnSecondary,
                      opacity: sync.isPending ? 0.6 : 1,
                      cursor: sync.isPending ? "wait" : "pointer",
                    }}
                  >
                    {sync.isPending ? "Syncing…" : "Sync now"}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      if (confirm("Disconnect GitHub? Your linked PRs and commits stay attached.")) {
                        disconnect.mutate();
                      }
                    }}
                    disabled={disconnect.isPending}
                    style={{ ...btnGhost, color: "#ee1d36", borderColor: "rgba(238,29,54,0.3)" }}
                  >
                    Disconnect
                  </button>
                </>
              )}
            </div>
          </>
        )}
      </section>
    </div>
  );
}

const card: CSSProperties = {
  background: "var(--canvas)",
  border: "1px solid var(--hairline)",
  borderRadius: 8,
  padding: 24,
};

const fieldRow: CSSProperties = { display: "block", marginBottom: 12 };

const fieldLabel: CSSProperties = {
  display: "block",
  fontSize: 11,
  fontWeight: 500,
  letterSpacing: 0.6,
  textTransform: "uppercase",
  color: "var(--mute)",
  marginBottom: 4,
};

const input: CSSProperties = {
  width: "100%",
  background: "var(--canvas)",
  border: "1px solid var(--hairline)",
  borderRadius: 4,
  padding: "8px 10px",
  fontFamily: "var(--font-sans)",
  fontSize: 14,
  color: "var(--ink)",
  outline: "none",
};

const hint: CSSProperties = {
  display: "block",
  marginTop: 6,
  fontSize: 12,
  color: "var(--mute)",
};

const code: CSSProperties = {
  fontFamily: "var(--font-mono)",
  fontSize: 12,
  background: "var(--surface-2)",
  padding: "1px 5px",
  borderRadius: 3,
};

const alertStyle: CSSProperties = {
  marginTop: 12,
  padding: "8px 12px",
  fontSize: 13,
  color: "#ee1d36",
  background: "rgba(238,29,54,0.06)",
  border: "1px solid rgba(238,29,54,0.3)",
  borderRadius: 4,
};

const btnPrimary: CSSProperties = {
  background: "var(--ink)",
  color: "var(--on-primary)",
  border: "none",
  borderRadius: 4,
  padding: "8px 16px",
  fontSize: 13,
  fontWeight: 500,
  cursor: "pointer",
};

const btnSecondary: CSSProperties = {
  background: "var(--canvas)",
  border: "1px solid var(--hairline)",
  borderRadius: 4,
  padding: "8px 14px",
  fontSize: 13,
  color: "var(--ink)",
  cursor: "pointer",
  fontWeight: 500,
};

const btnGhost: CSSProperties = {
  background: "transparent",
  border: "1px solid var(--hairline)",
  borderRadius: 4,
  padding: "8px 14px",
  fontSize: 13,
  cursor: "pointer",
};
